import * as moment from "moment";
import "moment/locale/de";
import "moment/locale/es";
import "moment/locale/fr";
import "moment/locale/it";
import "moment/locale/pt";
import "moment/locale/ja";

/**
 * formatDate returns formated date using moment.js
 * It takes a @date as input and returns the date formatted as "MMM DD, YYYY".
 */
const formatDate = (date, locale = "") => {
    locale && moment.locale(locale);
    return moment.utc(date).format("ll");
};

/**
 * formatDate returns formated date using moment.js
 * It takes a @date as input and returns the date formatted as "MMMM DD, YYYY".
 */
const formatDatePost = (date, locale = "") => {
    locale && moment.locale(locale);
    return moment.utc(date).format("ll");
};

/**
 * isValidDate checks if the provided date is a valid ISO 8601 date using moment.js
 * It takes a @date as input and returns true if the date is valid, false otherwise
 */
const isValidDate = (date, locale = "") => {
    locale && moment.locale(locale);
    return moment.utc(date, moment.ISO_8601).isValid();
};

/**
 * Compare the start and end dates to see if they are on the same day.
 *
 * @param {Date} startDate - The start date of the range.
 * @param {Date} endDate - The end date of the range.
 * @returns {boolean} True if the dates are on the same day, false if not.
 */
const isSameDay = (startDate, endDate) =>
    startDate && endDate && moment.utc(startDate).isSame(moment.utc(endDate), "day");

/**
 * formatDateText formats a date using the specified format using moment.js
 * It takes a @date and a format as input. If the date is invalid, it returns null
 * Otherwise, it returns the date formatted according to the provided @format
 * The default format is "MMM DD, YYYY".
 */
const formatDateText = (date, format = "ll", locale = "") => {
    if (!isValidDate(date)) {
        return null;
    }
    locale && moment.locale(locale);
    return moment.utc(date).format(format);
};

/**
 * formatDateRange formats a date range using moment.js
 * It takes a @startDate and an @endDate as input
 * If both dates are provided, it returns a string representing the date range in the format "MMM DD - MMM DD, YYYY"
 * If both dates are identical, then it returns a string representing the date in the format "MMM DD, YYYY"
 * If only the start date is provided, then it returns a string representing the start date in the format "MMM DD, YYYY"
 * Otherwise, it returns an empty string
 */
const formatDateRange = (startDate, endDate, locale = "") => {
    locale && moment.locale(locale);
    let output = "";
    if (startDate && endDate) {
        const startDateStr = moment.utc(startDate).format("MMM DD");
        const endDateStr = formatDate(endDate);
        output = isSameDay(startDate, endDate) ? endDateStr : `${startDateStr} - ${endDateStr}`;
    } else if (startDate) {
        output = formatDate(startDate);
    }
    return output;
};

/**
 * fromYear converts a year to an ISO string using JavaScript's Date object
 * It takes a @year as input and returns the year converted to an ISO string
 */
const fromYear = (year) => new Date(year.toString()).toISOString();

/**
 * toYear extracts the year from a date string using JavaScript's Date object
 * It takes a @dateStr as input and returns the year of the date string
 */
const toYear = (dateStr) => new Date(dateStr).getFullYear().toString();

export { formatDate, formatDateText, formatDateRange, fromYear, toYear, formatDatePost, isSameDay };
